import { COLORS } from './chartColors';
import { sortLabelsChronologically } from './helpers/dateHelpers';
import { parseDataKey } from './helpers/dataHelpers';

interface SelectedEntity {
  id: number;
  name: string;
}

interface ApplicationRecord {
  id: string;
  first_application_to_hired?: number;
}

interface ApplicationData {
  data: ApplicationRecord[] | { reportNotFound: boolean };
}

interface MonthlyAverages {
  [monthLabel: string]: number;
}

export const jobFirstApplicationToHiredTransformer = (
  data: Record<string, ApplicationData>,
  selectedEntities: SelectedEntity[]
) => {
  const monthlyAverages: MonthlyAverages = {};

  Object.entries(data).forEach(([key, value]) => {
    const { monthLabel, shouldInclude } = parseDataKey(key, selectedEntities);

    if (!shouldInclude) return;

    if (Array.isArray(value.data)) {
      // Group data by job id
      const jobData = value.data.reduce(
        (acc, record) => {
          if (!record.id) return acc;
          if (!acc[record.id]) {
            acc[record.id] = {
              times: []
            };
          }
          if (record.first_application_to_hired) {
            const value = parseFloat(record.first_application_to_hired as unknown as string);
            if (!isNaN(value)) {
              acc[record.id].times.push(value);
            }
          }
          return acc;
        },
        {} as Record<string, { times: number[] }>
      );

      // Calculate average
      const allTimes = Object.values(jobData)
        .flatMap((job) => job.times)
        .filter(Boolean);

      if (allTimes.length > 0) {
        const average = allTimes.reduce((sum, time) => sum + time, 0) / allTimes.length;
        monthlyAverages[monthLabel] = parseFloat((average / (24 * 60 * 60)).toFixed(1)); // Convert seconds to days and round to 1 decimal
      }
    } else if (!value.data.reportNotFound) {
      return;
    }
  });

  const sortedLabels = sortLabelsChronologically(Object.keys(monthlyAverages));

  return {
    labels: sortedLabels,
    datasets: [
      {
        label: 'Average time (Days)',
        data: sortedLabels.map((label) => monthlyAverages[label]),
        borderWidth: 1,
        backgroundColor: COLORS[0],
        fill: false
      }
    ]
  };
};
